import { isGroupsCategory } from '@orus.eu/activity-groups'
import { parseAmount } from '@orus.eu/amount'
import type { SubscriptionInjectableParams } from '@orus.eu/backend'
import {
  inferCompanyIdNumberFromSiret,
  parseCifValue,
  parseNifValue,
  parseSirenValue,
} from '@orus.eu/company-id-number'
import { NO_REFERRAL_CODE, type EmbeddingPartner } from '@orus.eu/dimensions'
import { productSelectionDimensionsByProduct } from '@orus.eu/dimensions-aggregate'
import { isEmailSyntaxValid } from '@orus.eu/email'
import { validatePhoneNumber } from '@orus.eu/pharaoh'
import { allProducts } from '@orus.eu/product'
import { failure, type Result, success } from '@orus.eu/result'
import { parseSiret } from '@orus.eu/siret'

import type { SearchParams } from '../routes/__root.tsx'

import {
  type InjectableUrlParam,
  LEGACY_PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME,
  PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME,
  type ProductSelectionDimensionName,
} from './subscription-injectable-url-params.ts'

export function getProductSelectionFromSearchParams(
  searchParams: Pick<SearchParams, InjectableUrlParam>,
  dimensionName: ProductSelectionDimensionName,
): boolean | undefined {
  return (
    readBooleanSearchParam(searchParams, PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME[dimensionName]) ??
    readBooleanSearchParam(searchParams, LEGACY_PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME[dimensionName])
  )
}

function readBooleanSearchParam(
  searchParams: Pick<SearchParams, InjectableUrlParam>,
  urlParam: InjectableUrlParam | undefined,
): boolean | undefined {
  if (!urlParam) return undefined
  const value = searchParams[urlParam]
  return typeof value === 'boolean' ? value : undefined
}

type SubscriptionInjectableParamsFromURLSearchParams = SubscriptionInjectableParams & {
  organizationTechnicalName?: string | undefined
}

export const SUBSCRIPTION_FUNNEL_PROPAGATED_KEYS: (keyof SearchParams)[] = [
  'activity_name',
  'embedding_partner',
  'utm_source',
  'utm_campaign',
  'siren',
  'siret',
  'ape',
  // a full page load re-reads these two from the URL only: without them the operating zone falls back to `fr`
  // and the funnel leaves iframe mode
  'operating_zone',
  'embed_in_iframe',
] as const

export const pickPropagatedSearchParams = (
  prev: SearchParams,
  source: (keyof SearchParams)[],
): Partial<SearchParams> => {
  return Object.fromEntries(source.map((key) => [key, prev[key]]))
}

export function getSubscriptionInjectableParamsFromURLSearchParams(
  searchParams: Pick<SearchParams, InjectableUrlParam>,
): Result<SubscriptionInjectableParamsFromURLSearchParams, { type: 'invalid-params' }> {
  const subscriptionInjectableParams: Mutable<SubscriptionInjectableParamsFromURLSearchParams> = {}

  const embeddingPartnerId = searchParams['embedding_partner']
  if (embeddingPartnerId) {
    const embeddingPartner: EmbeddingPartner = { id: embeddingPartnerId }
    subscriptionInjectableParams.embeddingPartner = embeddingPartner
  } else {
    const organizationTechnicalName = searchParams['organization']
    if (organizationTechnicalName) {
      subscriptionInjectableParams.organizationTechnicalName = organizationTechnicalName
    }
  }

  const partnerId = searchParams['partner_id']
  if (partnerId) {
    subscriptionInjectableParams.partner_id = partnerId
  }

  const activity = searchParams['activity']
  if (activity) {
    subscriptionInjectableParams.activity = activity
  } else {
    const category = searchParams['category']
    if (category) {
      if (isGroupsCategory(category)) {
        subscriptionInjectableParams.category = category
      } else {
        return failure({ type: 'invalid-params' })
      }
    }
  }

  const firstName = searchParams['first_name']
  if (firstName) {
    subscriptionInjectableParams.firstName = firstName
  }

  const lastName = searchParams['last_name']
  if (lastName) {
    subscriptionInjectableParams.lastName = lastName
  }

  const siret = searchParams['siret']
  const parsedSiret = siret ? parseSiret(siret) : null
  if (parsedSiret) {
    subscriptionInjectableParams.siretData = { type: 'provided', siret: parsedSiret }
    subscriptionInjectableParams.companyIdNumber = inferCompanyIdNumberFromSiret(parsedSiret)
  } else {
    const siren = searchParams['siren']
    const parsedSiren = siren ? parseSirenValue(siren) : null
    if (parsedSiren) {
      subscriptionInjectableParams.companyIdNumber = { type: 'siren', siren: parsedSiren }
    }
    const cif = searchParams['cif']
    const parsedCif = cif ? parseCifValue(cif) : null
    if (parsedCif) {
      subscriptionInjectableParams.companyIdNumber = { type: 'cif', cif: parsedCif }
    }
    const nif = searchParams['nif']
    const parsedNif = nif ? parseNifValue(nif) : null
    if (parsedNif) {
      subscriptionInjectableParams.companyIdNumber = { type: 'nif', nif: parsedNif }
    }
  }

  const email = searchParams['email']?.toLocaleLowerCase()
  if (email && isEmailSyntaxValid(email)) {
    subscriptionInjectableParams.email = email
  }

  const phone = searchParams['phone']
  if (
    phone &&
    validatePhoneNumber(
      phone,
      // language is just used to format the error message if the number is invalid, but
      // we're not using the message here, so any language can do
      'fr',
    ).ok
  ) {
    subscriptionInjectableParams.phone = phone
  }

  const revenue = searchParams['revenue']
  if (revenue) {
    const revenueAmount = parseAmount(revenue)
    if (revenueAmount) {
      subscriptionInjectableParams.estimatedRevenue = revenueAmount
    }
  }

  const discount = searchParams['discount']
  if (discount === 'partner-disc221220') {
    subscriptionInjectableParams.discountCode = discount
  }

  const referralCode = searchParams['referral_code']
  if (referralCode) {
    subscriptionInjectableParams.referralCode = referralCode
  }

  const operatingZone = searchParams['operating_zone']
  if (operatingZone) {
    subscriptionInjectableParams.operatingZone = operatingZone
  }

  const rcpaPersona = searchParams['rcpa_persona']
  if (rcpaPersona) {
    subscriptionInjectableParams.rcpaPersona = rcpaPersona
  }

  for (const product of allProducts) {
    const dimensionName = productSelectionDimensionsByProduct[product].name
    subscriptionInjectableParams[dimensionName] = getProductSelectionFromSearchParams(searchParams, dimensionName)
  }

  const socialSecurityRegime = searchParams['social_security_regime']
  if (socialSecurityRegime) {
    subscriptionInjectableParams.socialSecurityRegime = socialSecurityRegime
  }

  const mrpSelected = searchParams['mrp_selected']
  if (mrpSelected) {
    subscriptionInjectableParams.mrpSelected = mrpSelected
  }

  return success(subscriptionInjectableParams)
}

export function omitReferralCodeFromInjectableParams<T extends { referralCode?: string | undefined }>(params: T): T {
  return { ...params, referralCode: NO_REFERRAL_CODE }
}

type Mutable<T> = {
  -readonly [P in keyof T]: T[P]
}
